import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Switch, Route, Redirect, Router } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import Helmet from 'react-helmet';

import ToolBar from '../components/Toolbar';
import SignIn from '../pages/SignIn';
import RecoveryPassword from '../pages/RecoveryPassword';
import Dashboard from '../pages/Dashboard';
import ResetPassword from '../pages/RecoveryPassword/ResetPassword';
import ListCourses from '../pages/ListCourses';
import Profile from '../pages/Profile';
import Lesson from '../pages/Course/Lesson';
import Certificate from '../pages/Course/Certificate';
import Appointments from '../pages/Appointments';
import AboutAppointments from '../pages/Appointments/AboutAppointments';
import HistoryAppointments from '../pages/Appointments/HistoryAppointments';
import NewAppointment from '../pages/Appointments/NewAppointment';
import NoMatch from '../pages/NoMatch';
// import ByGuid from '../pages/SignIn/ByGuid';

import historyConfig from './history';

const defaultFavicon =
  'https://blob.contato.io/machines-bonus-images/all-images/favicon.png';

const Routes = () => {
  const {
    auth: { isAuthenticated },
    product: { favicon: customFavicon, showAppointments, initialPage },
    appointments: {
      infos: {
        daysToRealease: daysToAllowAppointments,
        loaded: appointmentsInfosLoaded,
      },
    },
  } = useSelector(state => state);
  const [favicon, setFavicon] = useState(defaultFavicon);

  useEffect(() => {
    setFavicon(customFavicon || defaultFavicon);
  }, [customFavicon]);

  const checkIfShowAppointments = useMemo(() => {
    if (!showAppointments) return false;
    if (appointmentsInfosLoaded && daysToAllowAppointments > 0) return false;
    return true;
  }, [showAppointments, daysToAllowAppointments, appointmentsInfosLoaded]);

  const initialPath = useMemo(() => {
    switch (initialPage) {
      case 'LIST':
        return '/list';
      default:
        return '/dashboard';
    }
  }, [initialPage]);

  return (
    <ConnectedRouter history={historyConfig}>
      <Router history={historyConfig} location="/">
        <Helmet>
          <link rel="icon" href={favicon} />
        </Helmet>
        <Switch>
          {/* <Route exact path="/login/byGuid/:guid" component={ByGuid} /> */}
          <PublicRoute exact path="/login" authenticated={isAuthenticated}>
            <SignIn />
          </PublicRoute>
          <PublicRoute
            exact
            path="/forgot-password"
            authenticated={isAuthenticated}
          >
            <RecoveryPassword />
          </PublicRoute>
          <PublicRoute path="/reset-password" authenticated={isAuthenticated}>
            <Route path="/reset-password/:token" component={ResetPassword} />
          </PublicRoute>
          <PrivateRoute path="/" authenticated={isAuthenticated}>
            <ToolBar />
            <Switch>
              <Route
                exact
                path="/"
                render={() => <Redirect to={{ pathname: initialPath }} />}
              />
              <Route path="/dashboard" component={Dashboard} />
              <Route path="/list" component={ListCourses} />
              <Route path="/profile" component={Profile} />
              <Route exact path="/course/:course" component={Lesson} />
              <Route
                exact
                path="/course/:course/certificate"
                component={Certificate}
              />
              <Route path="/course/:course/:lesson" component={Lesson} />
              {checkIfShowAppointments && (
                <Route
                  path="/appointments"
                  render={({ match: { url } }) => (
                    <Appointments>
                      <Route
                        exact
                        path={`${url}/`}
                        component={AboutAppointments}
                      />
                      <Route
                        exact
                        path={`${url}/history`}
                        component={HistoryAppointments}
                      />
                      <Route
                        exact
                        path={`${url}/new`}
                        render={() => <Redirect to={`${url}/new/schedule`} />}
                      />
                      <Route
                        path={`${url}/new/:step`}
                        component={NewAppointment}
                      />
                    </Appointments>
                  )}
                />
              )}
              <Route path="/not-found" component={NoMatch} />
              <Route path="*" component={NoMatch} />
            </Switch>
          </PrivateRoute>
        </Switch>
      </Router>
    </ConnectedRouter>
  );
};

function PublicRoute({ children, authenticated, ...rest }) {
  const { location: stateLocation } = useSelector(state => state.router);
  const from = stateLocation.state
    ? stateLocation.state.from.pathname
    : stateLocation.pathname;

  return (
    <Route
      render={
        ({ location }) =>
          authenticated ? (
            <Redirect
              to={{
                pathname: from.includes('/login') ? '' : from,
                state: { from: location },
              }}
            />
          ) : (
            children
          )
        // eslint-disable-next-line react/jsx-curly-newline
      }
      {...rest}
    />
  );
}

function PrivateRoute({ children, authenticated, ...rest }) {
  return (
    <Route
      render={
        ({ location }) =>
          authenticated ? (
            children
          ) : (
            <Redirect to={{ pathname: '/login', state: { from: location } }} />
          )
        // eslint-disable-next-line react/jsx-curly-newline
      }
      {...rest}
    />
  );
}

export default Routes;
